<!-- src/components/common/UserInfo.vue -->
<template>
  <Popover v-if="user" class="relative" v-model="isOpen">
    <PopoverButton
      class="group inline-flex items-center gap-x-2 sm:gap-x-3 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-medium text-gray-85 hover:bg-primary-1 transition-colors"
    >
      <div
        class="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-primary-1"
      >
        <component
          :is="user.icon || UserOutlined"
          class="h-4 w-4 sm:h-5 sm:w-5 text-primary-6"
        />
      </div>
      <span class="hidden sm:block truncate max-w-[120px]">{{
        user.name
      }}</span>
    </PopoverButton>

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <PopoverPanel class="absolute right-0 z-50 mt-2 transform">
        <div
          class="w-[280px] sm:w-[320px] overflow-hidden rounded-xl bg-white shadow-elevated ring-1 ring-black/5"
        >
          <!-- 用户信息头部 -->
          <div class="px-4 sm:px-6 py-4 sm:py-5">
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-primary-1"
              >
                <component
                  :is="user.icon || UserOutlined"
                  class="h-5 w-5 sm:h-6 sm:w-6 text-primary-6"
                />
              </div>
              <div class="min-w-0 flex-1">
                <h4
                  class="text-base sm:text-base font-semibold leading-6 text-gray-85"
                >
                  {{ user.name }}
                </h4>
                <div class="mt-1 sm:mt-1.5 flex items-center gap-2">
                  <span class="text-sm sm:text-sm text-gray-45">{{
                    user.username
                  }}</span>
                  <span
                    class="inline-flex items-center rounded-full px-2 sm:px-2.5 py-0.5 text-sm font-medium"
                    :style="{
                      backgroundColor: user.roleColor + '15',
                      color: user.roleColor,
                    }"
                  >
                    {{ user.useTranslation ? t(user.role) : user.role }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- 学籍信息 -->
          <div class="mx-4 sm:mx-6 rounded-lg bg-gray-2/50 p-3 sm:p-4">
            <div class="flex items-center justify-between py-1">
              <span class="text-sm sm:text-sm text-gray-45">{{
                t("user.grade")
              }}</span>
              <span class="text-sm sm:text-sm font-medium text-gray-85">{{
                user.grade
              }}</span>
            </div>
            <div class="flex items-center justify-between py-1">
              <span class="text-sm sm:text-sm text-gray-45">{{
                t("user.class")
              }}</span>
              <span class="text-sm sm:text-sm font-medium text-gray-85">{{
                user.major_class
              }}</span>
            </div>
          </div>

          <!-- 常规操作菜单 -->
          <div class="px-3 sm:px-4 py-3 sm:py-4">
            <div
              v-for="item in menuItems"
              :key="item.name"
              class="group relative flex items-center gap-x-2 sm:gap-x-3 rounded-lg p-2 sm:p-2.5 text-sm sm:text-sm leading-6 hover:bg-gray-2"
            >
              <div
                class="flex h-8 w-8 sm:h-9 sm:w-9 flex-none items-center justify-center rounded-lg bg-primary-1 group-hover:bg-white"
              >
                <component
                  :is="item.icon"
                  class="h-4 w-4 sm:h-5 sm:w-5 text-primary-6"
                  aria-hidden="true"
                />
              </div>
              <a
                href="#"
                class="flex-1 font-semibold text-gray-85"
                @click.prevent="item.onClick"
              >
                {{ item.name }}
                <span class="absolute inset-0"></span>
              </a>
            </div>
          </div>

          <!-- 退出登录 -->
          <div class="bg-gray-2">
            <a
              href="#"
              @click.prevent="handleLogout"
              class="flex items-center justify-center gap-x-1.5 sm:gap-x-2 p-2.5 sm:p-3 text-sm sm:text-sm font-semibold text-gray-65 hover:bg-gray-4 hover:text-gray-85 transition-colors"
            >
              <ArrowRightOnRectangleIcon
                class="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 text-gray-45"
                aria-hidden="true"
              />
              {{ t("user.logout") }}
            </a>
          </div>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>

  <!-- WeChat Unbind Confirmation Modal -->
  <a-modal
    v-model:open="wechatUnbindModalVisible"
    :title="t('security.wechat.unbindConfirmTitle')"
    :confirm-loading="unbindingWechat"
    @ok="handleWechatUnbind"
    @cancel="cancelWechatUnbind"
  >
    <div class="space-y-4">
      <div class="flex items-start space-x-3">
        <ExclamationTriangleIcon
          class="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5"
        />
        <div>
          <p class="text-sm text-gray-900">
            {{ t("user.wechatRebindConfirm") }}
          </p>
          <p class="text-xs text-gray-500 mt-2">
            {{ t("user.wechatRebindDesc") }}
          </p>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex gap-2">
        <a-button @click="cancelWechatUnbind">
          {{ t("common.cancel") }}
        </a-button>
        <a-button
          type="primary"
          danger
          :loading="unbindingWechat"
          @click="handleWechatUnbind"
        >
          {{ t("user.confirmWechatRebind") }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { notificationService } from "@/services/notificationService";
import { jwtService } from "@/services/jwt";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { FingerPrintIcon } from "@heroicons/vue/24/outline";
import {
  KeyIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  UserIcon as UserOutlined,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/auth";
import { passkeySupport } from "@/services/passkeySupportService";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();
const isOpen = ref(false);

// 使用 authStore 的响应式用户状态
const user = computed(() => {
  const storeUser = authStore.user;
  if (!storeUser) return null;

  // 从 JWT token 中获取 role 信息
  const token = jwtService.getToken();
  let jwtRole = "student"; // 默认值

  if (token) {
    const payload = jwtService.parseToken(token);
    jwtRole = payload?.role || "student";
  }

  const { role, icon, color, useTranslation } = getUserRoleInfo({
    role: jwtRole,
    is_superuser: storeUser.is_superuser,
    is_staff: storeUser.is_staff,
  });

  return {
    id: storeUser.username, // 使用 username 作为 id
    username: storeUser.username,
    name: storeUser.name,
    grade: storeUser.grade,
    major_class: storeUser.major_class,
    role,
    roleColor: color,
    icon,
    is_staff: storeUser.is_staff,
    is_superuser: storeUser.is_superuser,
    useTranslation,
  };
});

// Passkey 可用性（环境支持 + 功能开关）
const isPasskeyAvailable = computed(
  () => passkeySupport.isEnabled() && passkeySupport.supportedRef.value === true,
);

// 定义菜单项
const menuItems = computed(() => [
  {
    name: t("user.changeEmail"),
    icon: EnvelopeIcon,
    onClick: handleEmailChange,
  },
  {
    name: t("user.changePhone"),
    icon: DevicePhoneMobileIcon,
    onClick: handlePhoneChange,
  },
  {
    name: t("user.changePassword"),
    icon: KeyIcon,
    onClick: handlePasswordChange,
  },
  {
    name: t("user.securityCenter"),
    icon: ShieldCheckIcon,
    onClick: handleSecurityCenter,
  },
  ...(isPasskeyAvailable.value
    ? [
        {
          name: t("user.registerPasskey"),
          icon: FingerPrintIcon,
          onClick: handlePasskeyRegister,
        },
      ]
    : []),
]);

// 获取用户角色和图标
function getUserRoleInfo(payload: {
  role: string;
  is_superuser: boolean;
  is_staff: boolean;
}) {
  let role = "user.roles.student";
  let icon = UserOutlined;
  let color = "#52c41a";
  let useTranslation = true;

  if (payload.role) {
    const roleStr = payload.role.toString();

    // 管理员角色判断
    if (
      roleStr === "管理员" ||
      roleStr.toLowerCase().includes("admin") ||
      roleStr === "admin" ||
      payload.is_superuser
    ) {
      role = "user.roles.admin";
      color = "#f5222d";
      icon = UserOutlined;
    }
    // 教师角色判断
    else if (
      roleStr === "教师" ||
      roleStr === "老师" ||
      roleStr.toLowerCase().includes("teacher") ||
      roleStr === "teacher" ||
      (payload.is_staff && !payload.is_superuser)
    ) {
      role = "user.roles.teacher";
      color = "#1890ff";
      icon = UserOutlined;
    }
    // 学生角色判断
    else if (
      roleStr === "学生" ||
      roleStr.toLowerCase().includes("student") ||
      roleStr === "student"
    ) {
      role = "user.roles.student";
      color = "#52c41a";
      icon = UserOutlined;
    }
    // 其他角色，直接显示原文
    else {
      role = roleStr;
      useTranslation = false;
      // 根据角色名生成颜色
      const hash = roleStr.split("").reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
      }, 0);
      const hue = Math.abs(hash) % 360;
      color = `hsl(${hue}, 70%, 45%)`;
      icon = UserOutlined;
    }
  } else {
    // 如果没有 role 字段，根据权限标志判断
    if (payload.is_superuser) {
      role = "user.roles.admin";
      color = "#f5222d";
      icon = UserOutlined;
    } else if (payload.is_staff) {
      role = "user.roles.teacher";
      color = "#1890ff";
      icon = UserOutlined;
    }
  }

  return { role, icon, color, useTranslation };
}

// 验证用户会话的统一方法
async function validateUserSession(): Promise<boolean> {
  return await authStore.validateSession();
}

onMounted(async () => {
  // 如果 authStore 中没有用户数据但有token，尝试初始化
  if (!authStore.user && jwtService.getToken()) {
    try {
      await authStore.fetchUserFromToken();
    } catch (error) {
      console.error("Failed to initialize user from token:", error);
    }
  }

  // 预检 Passkey 支持
  try {
    await passkeySupport.detectSupport();
  } catch {
    // ignore
  }
});

// 处理修改邮箱
const handleEmailChange = async () => {
  if (await validateUserSession()) {
    isOpen.value = false; // 关闭菜单
    router.push("/email");
  }
};

// 处理修改手机号
const handlePhoneChange = async () => {
  if (await validateUserSession()) {
    isOpen.value = false; // 关闭菜单
    router.push("/phone");
  }
};

// 处理修改密码
const handlePasswordChange = async () => {
  if (await validateUserSession()) {
    isOpen.value = false; // 关闭菜单
    router.push("/password/modify");
  }
};

// 处理Passkey注册
const handlePasskeyRegister = async () => {
  if (await validateUserSession()) {
    isOpen.value = false; // 关闭菜单
    router.push("/passkey/register");
  }
};

// 添加导航方法
function handleSecurityCenter() {
  isOpen.value = false; // 关闭菜单
  router.push("/security-center");
}

// WeChat rebinding modal state
const wechatUnbindModalVisible = ref(false);
const wechatOpenidForUnbind = ref<string | null>(null);
const unbindingWechat = ref(false);

// 处理退出登录
const handleLogout = async () => {
  try {
    isOpen.value = false; // 先关闭菜单

    // 检查是否有微信openid需要换绑
    const { wechatService } = await import("@/services/wechatService");
    const wechatOpenid = wechatService.getOpenidFromStorage();

    if (wechatOpenid) {
      // 有微信openid，需要先解绑才能登出
      wechatOpenidForUnbind.value = wechatOpenid;
      wechatUnbindModalVisible.value = true;
    } else {
      // 没有微信openid，正常登出
      await authStore.logout();
      notificationService.success("user.logoutSuccess");
    }
  } catch (error) {
    console.error("Logout error:", error);
    notificationService.error("user.logoutError");
  }
};

// 处理微信解绑
const handleWechatUnbind = async () => {
  if (!wechatOpenidForUnbind.value) return;

  try {
    unbindingWechat.value = true;
    const { unbindWechatOpenid } = await import("@/api/wechat");

    await unbindWechatOpenid(wechatOpenidForUnbind.value);

    notificationService.success("security.wechat.unbindSuccess");
    wechatUnbindModalVisible.value = false;

    // 解绑成功后开始换绑流程
    authStore.startWechatRebinding(wechatOpenidForUnbind.value);

    // 跳转到专门的微信换绑页面
    router.push({
      name: "wechat-rebind",
      query: route.query, // 传递当前查询参数，如redirect等
    });
  } catch (error: unknown) {
    console.error("微信解绑失败:", error);
    const axiosError = error as { response?: { data?: { message?: string } } };
    notificationService.error(
      axiosError.response?.data?.message || "security.wechat.unbindError",
    );
  } finally {
    unbindingWechat.value = false;
  }
};

// 取消微信解绑
const cancelWechatUnbind = () => {
  wechatUnbindModalVisible.value = false;
  wechatOpenidForUnbind.value = null;
};
</script>
